import type { CinematicOverrides, CinematicSequence, CinematicWaypoint } from '../../../../renderer/src/cinematic-sequence';
import { MAX_CINEMATIC_WAYPOINTS } from '../../../../renderer/src/cinematic-sequence';
import { cinematicAnchorAt, cinematicTimeline } from '../../domain/cinematic-timeline';
import { cinematicPropertyTimeline } from '../../domain/cinematic-property-timeline';
import type { RidePoint } from '../../domain/ride';
import { sceneFrameProgress } from '../scene-keyframes';

export const scenePropertyToken = (id: string, property: keyof CinematicOverrides) => `${property}:${id}`;
export function readScenePropertyToken(token: string) {
  const separator = token.indexOf(':');
  return separator < 0 ? { id: token, property: undefined }
    : { id: token.slice(separator + 1), property: token.slice(0, separator) as keyof CinematicOverrides };
}

/** Detach only the edited property; retain every other value, anchor and transition. */
export function isolateSceneProperty(sequence: CinematicSequence, id: string, property: keyof CinematicOverrides, newId: string) {
  const target = sequence.waypoints.find(item => item.id === id);
  if (!target || target.overrides[property] === undefined) throw new Error('Choose an existing property key.');
  if (Object.keys(target.overrides).length === 1) return { sequence, key: target };
  if (sequence.waypoints.length >= MAX_CINEMATIC_WAYPOINTS) throw new Error('Remove a key before adding another property key.');
  const overrides = { ...target.overrides }; delete overrides[property];
  const key: CinematicWaypoint = { ...target, id: newId, overrides: { [property]: target.overrides[property] } };
  return { sequence: { ...sequence, waypoints: [...sequence.waypoints.map(item => item.id === id ? { ...item, overrides } : item), key] }, key };
}

export function moveSceneProperty(points: readonly RidePoint[], sequence: CinematicSequence, id: string, property: keyof CinematicOverrides,
  seconds: number, duration: number, newId: string) {
  if (!Number.isFinite(seconds)) throw new Error('Choose a valid film time.');
  const filmProgress = sceneFrameProgress(seconds / duration, duration), frame = Math.round(filmProgress * (duration * 30 - 1));
  const timeline = cinematicTimeline(points, sequence, duration);
  if (timeline.some(item => item.waypoint.id !== id && item.waypoint.overrides[property] !== undefined
    && Math.round(item.seconds / duration * (duration * 30 - 1)) === frame)) throw new Error('A key for this property already occupies that frame.');
  const isolated = isolateSceneProperty(sequence, id, property, newId);
  const moved = { ...isolated.sequence, waypoints: isolated.sequence.waypoints.map(item => item.id === isolated.key.id
    ? { ...item, filmProgress, anchor: cinematicAnchorAt(points, filmProgress, duration) } : item) };
  return { sequence: { ...moved, waypoints: cinematicTimeline(points, moved, duration).map(item => item.waypoint) }, id: isolated.key.id, progress: filmProgress };
}

export function patchSceneProperty(sequence: CinematicSequence, id: string, property: keyof CinematicOverrides,
  transition: CinematicWaypoint['transition'], newId: string) {
  const isolated = isolateSceneProperty(sequence, id, property, newId);
  return { sequence: { ...isolated.sequence, waypoints: isolated.sequence.waypoints.map(item => item.id === isolated.key.id ? { ...item, transition } : item) }, id: isolated.key.id };
}

export function propertyTransitionAt(points: readonly RidePoint[], sequence: CinematicSequence, id: string, property: keyof CinematicOverrides, duration: number) {
  return cinematicPropertyTimeline(cinematicTimeline(points, sequence, duration), property, duration).find(item => item.waypoint.id === id);
}
